// Page t("reconciliationPage.title") (Lot 5.4) — import CSV + matching manuel.
//
// Flow user :
//  1. Upload d'un CSV de relevé (drag-drop ou bouton)
//  2. Preview : une ligne par crédit, avec jusqu'à 3 suggestions de facture
//  3. L'utilisateur choisit (ou laisse vide) la facture associée à chaque ligne
//  4. Bouton "Appliquer" → crée les Payments correspondants
//
// Principe : jamais de rapprochement silencieux. Tant que l'utilisateur n'a
// pas cliqué Appliquer, aucune écriture en base.

import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  reconciliationApi,
  type ReconciliationApplyResponse,
  type ReconciliationPreview,
  type ReconciliationRow,
} from "../api/reconciliation";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { useAuthStore } from "../stores/auth";

function formatAmount(value: string, currency: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return `${value} ${currency}`;
  return `${n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

function formatDate(iso: string): string {
  // iso = "YYYY-MM-DD" — on reformate en JJ/MM/AAAA pour la lisibilité.
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

type Confirmation = {
  row_id: number;
  invoice_id: string | null;
};

export function ReconciliationPage() {
  const { t } = useTranslation();
  const currentRole = useAuthStore((s) => s.currentRole);
  const canWrite = currentRole === "admin" || currentRole === "comptable";

  const [preview, setPreview] = useState<ReconciliationPreview | null>(null);
  const [choices, setChoices] = useState<Record<number, string>>({});
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyResult, setApplyResult] =
    useState<ReconciliationApplyResponse | null>(null);
  const [applying, setApplying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function onFileSelected(file: File) {
    setError(null);
    setApplyResult(null);
    setUploading(true);
    try {
      const result = await reconciliationApi.previewFromFile(file);
      setPreview(result);
      setChoices({});
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Impossible de parser le fichier";
      setError(msg);
      setPreview(null);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const confirmations: Confirmation[] = useMemo(() => {
    if (!preview) return [];
    return preview.rows.map((r) => ({
      row_id: r.transaction.row_id,
      invoice_id: choices[r.transaction.row_id] ?? null,
    }));
  }, [preview, choices]);

  const selectedCount = confirmations.filter((c) => c.invoice_id).length;

  async function onApply() {
    if (!preview) return;
    setApplying(true);
    setError(null);
    try {
      const payload = {
        transactions: preview.rows.map((r) => r.transaction),
        confirmations: confirmations
          .filter((c) => c.invoice_id)
          .map((c) => ({
            row_id: c.row_id,
            invoice_id: c.invoice_id as string,
          })),
      };
      const result = await reconciliationApi.apply(payload);
      setApplyResult(result);
      // Réinitialise les choix des lignes traitées avec succès — évite un
      // double-clic qui renverrait la même confirmation.
      const appliedRowIds = new Set(result.applied.map((a) => a.row_id));
      setChoices((prev) => {
        const next: Record<number, string> = {};
        for (const [k, v] of Object.entries(prev)) {
          if (!appliedRowIds.has(Number(k))) next[Number(k)] = v;
        }
        return next;
      });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors de l'application";
      setError(msg);
    } finally {
      setApplying(false);
    }
  }

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("reconciliationPage.title")}
        </h1>
        <p className="text-sm text-slate-500">
          {t("reconciliationPage.subtitle")}
        </p>
      </header>

      {!canWrite && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md mb-4">
          Seuls les rôles Administrateur et Comptable peuvent importer un
          relevé.
        </div>
      )}

      {canWrite && (
        <section className="bg-white border border-slate-200 rounded-md p-4 mb-6">
          <div className="flex items-center gap-4">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                id="bank-csv"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFileSelected(f);
                }}
              />
              <label
                htmlFor="bank-csv"
                className={[
                  "inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium cursor-pointer text-sm",
                  uploading
                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                    : "bg-brand-500 text-white hover:bg-brand-600",
                ].join(" ")}
              >
                {uploading ? t("common.loading") : t("reconciliationPage.importCsv")}
              </label>
            </div>
            <p className="text-xs text-slate-500">
              {t("reconciliationPage.formats")}
            </p>
          </div>
          {error && (
            <div
              className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
              role="alert"
            >
              {error}
            </div>
          )}
        </section>
      )}

      {preview && (
        <>
          {preview.warnings.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
              <ul className="list-disc list-inside space-y-1">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <section className="bg-white border border-slate-200 rounded-md overflow-hidden mb-6">
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
              <div>
                <h2 className="text-base font-medium text-slate-800">
                  {preview.rows.length} crédit
                  {preview.rows.length > 1 ? "s" : ""} détecté
                  {preview.rows.length > 1 ? "s" : ""}
                </h2>
                <p className="text-xs text-slate-500">
                  Séparateur : <code>{preview.detected_separator}</code>
                  {preview.skipped_rows > 0 && (
                    <> — {preview.skipped_rows} ligne(s) ignorée(s)</>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={onApply}
                disabled={applying || selectedCount === 0}
                className={[
                  "px-4 py-2 rounded-md text-sm font-medium",
                  applying || selectedCount === 0
                    ? "bg-slate-200 text-slate-500 cursor-not-allowed"
                    : "bg-emerald-600 text-white hover:bg-emerald-700",
                ].join(" ")}
              >
                {applying
                  ? "Application…"
                  : `Appliquer (${selectedCount} sélection${selectedCount > 1 ? "s" : ""})`}
              </button>
            </header>

            {preview.rows.length === 0 ? (
              <p className="p-6 text-sm text-slate-500 text-center">
                {t("reconciliationPage.noItems")}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Libellé</th>
                    <th className="text-right px-4 py-2">Montant</th>
                    <th className="text-left px-4 py-2">Facture à associer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((row) => (
                    <ReconciliationRowView
                      key={row.transaction.row_id}
                      row={row}
                      selected={choices[row.transaction.row_id] ?? ""}
                      onSelect={(invoiceId) =>
                        setChoices((prev) => {
                          const next = { ...prev };
                          if (invoiceId) {
                            next[row.transaction.row_id] = invoiceId;
                          } else {
                            delete next[row.transaction.row_id];
                          }
                          return next;
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {applyResult && (
        <section
          className="bg-white border border-slate-200 rounded-md p-4"
          aria-label="Résultat de l'application"
        >
          <h2 className="text-base font-medium text-slate-800 mb-2">
            Résultat
          </h2>
          {applyResult.applied.length > 0 && (
            <div className="mb-3">
              <p className="text-sm text-emerald-700 mb-2">
                {applyResult.applied.length} paiement
                {applyResult.applied.length > 1 ? "s" : ""} créé
                {applyResult.applied.length > 1 ? "s" : ""}.
              </p>
              <ul className="space-y-1 text-sm">
                {applyResult.applied.map((a) => (
                  <li key={a.payment_id} className="flex items-center gap-3">
                    <span className="text-emerald-600">✓</span>
                    <Link
                      to={`/invoices/${a.invoice_id}`}
                      className="text-brand-600 hover:underline font-medium"
                    >
                      {a.invoice_reference}
                    </Link>
                    <span className="text-slate-600">
                      {formatAmount(a.amount, "EUR")} — statut{" "}
                      <code>{a.new_invoice_status}</code>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {applyResult.errors.length > 0 && (
            <div>
              <p className="text-sm text-red-700 mb-2">
                {applyResult.errors.length} erreur
                {applyResult.errors.length > 1 ? "s" : ""} :
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                {applyResult.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </AppShell>
  );
}

type RowProps = {
  row: ReconciliationRow;
  selected: string;
  onSelect: (invoiceId: string) => void;
};

function ReconciliationRowView({ row, selected, onSelect }: RowProps) {
  const { t } = useTranslation();
  const topSuggestion = row.suggestions[0];
  return (
    <tr>
      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
        {formatDate(row.transaction.date)}
      </td>
      <td className="px-4 py-3 text-slate-800">
        <div className="font-medium">{row.transaction.label}</div>
        {topSuggestion && (
          <div className="text-xs text-slate-500">
            Suggestion : {topSuggestion.reason}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-right text-slate-800 whitespace-nowrap tabular-nums">
        {formatAmount(row.transaction.amount, row.transaction.currency)}
      </td>
      <td className="px-4 py-3">
        {row.suggestions.length === 0 ? (
          <span className="text-xs text-slate-400">
            {t("reconciliationPage.noItems")}
          </span>
        ) : (
          <select
            aria-label={`Facture pour la ligne du ${row.transaction.date}`}
            value={selected}
            onChange={(e) => onSelect(e.target.value)}
            className="block w-full rounded-md border border-slate-300 text-sm px-2 py-1"
          >
            <option value="">— Ne pas associer —</option>
            {row.suggestions.map((s) => (
              <option key={s.invoice_id} value={s.invoice_id}>
                {s.invoice_reference} · {s.client_name} ·{" "}
                {formatAmount(s.remaining, s.currency)} (score {s.score})
              </option>
            ))}
          </select>
        )}
      </td>
    </tr>
  );
}
